<template>
  <sfs-form-modal
    :id="modalId"
    :title="modalTitle"
  >
    <b-form @submit.stop.prevent="handleSubmit">
      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection"
        class="mb-3"
      >
        <b-col cols="12">
          <b-alert
            show
            variant="info"
            class="mb-0"
          >
            Work order assignments will be processed in the background. You can continue working while they complete.
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection"
        class="mb-3 text-center"
      >
        <b-col cols="12">
          The selected Work Orders will be assigned using the values below.
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection && nonAssignableWorkOrders.length"
        class="mb-2"
      >
        <b-col>
          <b-alert
            show
            variant="warning"
          >
            The selected Work Orders can't be assigned and will be skipped:
            {{ nonAssignableWorkOrders.map((wo) => wo.number || `#${wo.id}`).join(', ') }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.date_bucket"
            label-for="work-order-date_bucket"
          >
            <sfs-date-range-picker
              id="work-order-date_bucket"
              v-model="$v.form.date_bucket.$model"
              :with-class="{
                'is-invalid':
                  ($v.form.date_bucket_start.$dirty && $v.form.date_bucket_start.$error) ||
                  ($v.form.date_bucket_end.$dirty && $v.form.date_bucket_end.$error),
              }"
              :class="{
                'is-invalid':
                  ($v.form.date_bucket_start.$dirty && $v.form.date_bucket_start.$error) ||
                  ($v.form.date_bucket_end.$dirty && $v.form.date_bucket_end.$error),
              }"
              placeholder="Select Dates"
              @on-change="onDateBucketChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.date_bucket_start.$dirty"
              id="work-order-date_bucket_start-feedback"
            >
              <span v-if="!$v.form.date_bucket_start.required">Please enter a start date.</span>
              <span v-if="!$v.form.date_bucket_start.serverFailed">{{ serverErrors.date_bucket_start }}</span>
            </b-form-invalid-feedback>
            <b-form-invalid-feedback
              v-if="$v.form.date_bucket_end.$dirty"
              id="work-order-date_bucket_end-feedback"
            >
              <span v-if="!$v.form.date_bucket_end.required">Please enter a end date.</span>
              <span v-if="!$v.form.date_bucket_end.serverFailed">{{ serverErrors.date_bucket_end }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.assigned_to_id"
            label-for="work-order-assign-assigned_to_id"
          >
            <user-select
              id="work-order-assign-assigned_to_id"
              :select-class="{ 'is-invalid': $v.form.assigned_to_id.$dirty && $v.form.assigned_to_id.$error }"
              select-label="name"
              :value="form.assigned_to"
              :filter-params="technicianSelectParams"
              @input="onAssignedToChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.assigned_to_id.$dirty"
              id="work-order-assign-assigned_to_id-feedback"
            >
              <span v-if="!$v.form.assigned_to_id.required">Please enter an assigned technician.</span>
              <span v-if="!$v.form.assigned_to_id.serverFailed">{{ serverErrors.assigned_to }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.done_by_id"
            label-for="work-order-assign-done_by_id"
          >
            <user-select
              id="work-order-assign-done_by_id"
              :select-class="{ 'is-invalid': $v.form.done_by_id.$dirty && $v.form.done_by_id.$error }"
              select-label="name"
              :disabled="!form.assigned_to_id"
              :technician-manager-id="form.assigned_to_id"
              :primary-option="form.assigned_to"
              :value="form.done_by"
              @input="onDoneByChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.done_by_id.$dirty"
              id="work-order-assign-done_by_id-feedback"
            >
              <span v-if="!$v.form.done_by_id.required">Please enter the technician the work is completed by.</span>
              <span v-if="!$v.form.done_by_id.serverFailed">{{ serverErrors.done_by }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          v-if="canAssignCost && !isBulkSelection"
        >
          <b-form-group
            :label="fieldLabels.cost"
            label-for="work-order-cost"
          >
            <b-input-group
              prepend="$"
              :class="{ 'is-invalid': $v.form.cost.$dirty && $v.form.cost.$error }"
            >
              <b-form-input
                id="work-order-cost"
                v-model="$v.form.cost.$model"
                placeholder="Optional"
                :state="$v.form.cost.$dirty ? !$v.form.cost.$error : null"
                type="text"
              />
            </b-input-group>
            <b-form-invalid-feedback
              v-if="$v.form.cost.$dirty"
              id="work-order-cost-feedback"
            >
              <span v-if="!$v.form.cost.decimal">Please enter a number.</span>
              <span v-if="!$v.form.cost.serverFailed">
                {{ serverErrors.cost_cents }}
              </span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label-for="work-order-assign-email_summary">
            <b-form-checkbox
              id="work-order-assign-email_summary"
              v-model="$v.form.email_summary.$model"
            >
              {{ fieldLabels.email_summary }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="work-order-assign-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="work-order-assign-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { required, decimal } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import { WORK_ORDER_FORM_LABELS } from '@/constants/workOrders';
import { USER_TECHNICIAN_CATEGORY } from '@/constants/users';
import UserSelect from '@/components/admin/users/Select.vue';

export default {
  name: 'WorkOrdersAssignModal',
  components: {
    UserSelect,
  },
  mixins: [formMixin, formModalMixin],
  props: {
    value: {
      type: Object,
      default: null,
    },
    workOrders: {
      type: Array,
      default: () => [],
    },
    canAssignCost: {
      type: Boolean,
      default: false,
    },
    email_summary: { type: Boolean, default: true },
  },
  data() {
    const source = this.value || {};
    return {
      form: {
        date_bucket_start: source.date_bucket_start,
        date_bucket_end: source.date_bucket_end,
        date_bucket: [
          source.date_bucket_start && this.$options.filters.dateFormat(source.date_bucket_start),
          source.date_bucket_end && this.$options.filters.dateFormat(source.date_bucket_end),
        ],
        assigned_to: source.assigned_to,
        assigned_to_id: source.assigned_to_id,
        done_by: source.done_by,
        done_by_id: source.done_by_id,
        cost: null,
        cost_cents: null,
        email_summary: this.email_summary,
      },
      fieldLabels: {
        ...WORK_ORDER_FORM_LABELS,
        cost: this.$store.getters.translate('work_orders.cost', 'Cost'),
        cost_cents: this.$store.getters.translate('work_orders.cost', 'Cost'),
        email_summary: 'Email Assignment Summary' },
      technicianSelectParams: { category: USER_TECHNICIAN_CATEGORY, active: true },
      modalId: 'work-order-assign-modal',
    };
  },
  computed: {
    isBulkSelection() {
      return Array.isArray(this.workOrders) && this.workOrders.length > 0;
    },
    modalTitle() {
      if (this.isBulkSelection) {
        return `Assign ${this.assignableWorkOrders.length} Work Orders`;
      }
      return 'Assign Work Order';
    },
    assignableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => this.$can('assign', workOrder));
    },
    nonAssignableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => !this.$can('assign', workOrder));
    },
  },
  validations: {
    form: {
      date_bucket_start: {
        required,
        serverFailed() {
          return !this.hasServerErrors('date_bucket_start');
        },
      },
      date_bucket_end: {
        required,
        serverFailed() {
          return !this.hasServerErrors('date_bucket_end');
        },
      },
      date_bucket: {},
      assigned_to_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('assigned_to');
        },
      },
      done_by_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('done_by');
        },
      },
      cost: {
        decimal,
        serverFailed() {
          return !this.hasServerErrors('cost_cents');
        },
      },
      email_summary: {},
    },
  },
  watch: {
    'form.cost': function (newCost) {
      if (newCost) {
        this.form.cost_cents = isNaN(newCost) ? null : Math.round(newCost * 100);
      } else {
        this.form.cost_cents = null;
      }
    },
  },
  methods: {
    onDateBucketChange(dates) {
      this.form.date_bucket_start = dates[0] || '';
      this.form.date_bucket_end = dates[1] || '';
    },
    onAssignedToChange(user) {
      this.form.assigned_to_id = user ? user.id : null;
      this.form.assigned_to = user;
      this.form.done_by_id = user ? user.id : null;
      this.form.done_by = user;
    },
    onDoneByChange(user) {
      this.form.done_by_id = user ? user.id : null;
      this.form.done_by = user;
    },
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }

      if (this.isBulkSelection) {
        if (!this.assignableWorkOrders.length) {
          return;
        }
        this.$workOrdersAPI
          .bulkAssignWorkOrders({}, this.buildBulkAssignPayload())
          .then((response) => {
            this.resetServerErrors();
            this.$v.$reset();
            this.showBackgroundProcessingToast();
            this.$emit('assigned', response);
            this.closeModal();
          })
          .catch((error) => {
            this.processServerErrors(error.response.data.errors);
          });
        return;
      }

      this.$workOrdersAPI
        .assign(this.value.id, this.form)
        .then((workOrder) => {
          this.resetServerErrors();
          this.$v.$reset();
          this.$emit('input', workOrder);
          this.closeModal();
        })
        .catch((error) => {
          this.processServerErrors(error.response.data.errors);
        });
    },
    buildBulkAssignPayload() {
      return {
        work_order_ids: this.assignableWorkOrders.map((workOrder) => Number(workOrder.id)),
        assigned_to_id: this.form.assigned_to_id,
        done_by_id: this.form.done_by_id,
        date_bucket_start: this.form.date_bucket_start,
        date_bucket_end: this.form.date_bucket_end,
        email_summary: this.form.email_summary,
      };
    },
    showBackgroundProcessingToast() {
      if (!this.$bvToast) {
        return;
      }

      this.$bvToast.toast('Work order assignments are being processed in the background.', {
        title: 'Assignments queued',
        toaster: 'b-toaster-bottom-right',
        variant: 'info',
        autoHideDelay: 5000,
        appendToast: false,
      });
    },
  },
};
</script>
